import React, { useState, useCallback, useEffect, useRef } from 'react'
import classnames from 'classnames'
import { ChevronLeftIcon, ChevronRightIcon } from '@toasttab/buffet-pui-icons'

import { formatImageURL } from 'src/lib/js/utilities'
import Image from 'shared/components/common/Image'
import { useImageAspectRatio } from 'src/shared/js/hooks/useImageAspectRatio'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'
import { ToastImageSet, getMenuItemSrcSet } from '../../MenuItemImage'
import { ThumbnailGallery } from './ThumbnailGallery'
import { DotIndicators } from './DotIndicators'
import { t } from 'src/translations'

type Props = {
  imageSetUrls: ToastImageSet[]
  itemName: string
}

export const ProductImageGallery = ({ imageSetUrls, itemName }: Props) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const { ooImageAspectRatio, ooImageAspectRatioThreshold } = useFlags()

  // Reset activeIndex when imageSetUrls changes (e.g., product/variant transitions)
  useEffect(() => {
    setActiveIndex(0)
  }, [imageSetUrls])

  const threshold = parseFloat(ooImageAspectRatioThreshold || '0.7') || 0.7
  const { aspectRatioClass: detectedAspectRatioClass, onImageLoad } =
    useImageAspectRatio(threshold)
  const imageAspectRatioClass = ooImageAspectRatio
    ? detectedAspectRatioClass
    : undefined

  const handlePrevious = useCallback(() => {
    setActiveIndex((prev) => (prev === 0 ? imageSetUrls.length - 1 : prev - 1))
  }, [imageSetUrls.length])

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev === imageSetUrls.length - 1 ? 0 : prev + 1))
  }, [imageSetUrls.length])

  const handleThumbnailClick = useCallback((index: number) => {
    setActiveIndex(index)
  }, [])

  // Touch swipe support for mobile with visual drag feedback
  const containerRef = useRef<HTMLDivElement>(null)
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const swipeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [dragOffset, setDragOffset] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (swipeTimer.current) {
        clearTimeout(swipeTimer.current)
      }
    }
  }, [])

  // Reset dragOffset when activeIndex changes (e.g., dot click)
  useEffect(() => {
    setDragOffset(0)
  }, [activeIndex])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null
    touchStartY.current = e.touches[0]?.clientY ?? null
    setIsTransitioning(false)
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (
      touchStartX.current === null ||
      touchStartY.current === null ||
      !e.touches[0]
    )
      return

    const dx = touchStartX.current - e.touches[0].clientX
    const dy = touchStartY.current - e.touches[0].clientY

    // If vertical movement is greater than horizontal, it's a scroll - ignore
    if (Math.abs(dy) > Math.abs(dx)) return

    setDragOffset(-dx)
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || !e.changedTouches[0]) return
      const diff = touchStartX.current - e.changedTouches[0].clientX
      const SWIPE_THRESHOLD = 50

      // Clear any existing timer
      if (swipeTimer.current) {
        clearTimeout(swipeTimer.current)
      }

      if (Math.abs(diff) > SWIPE_THRESHOLD) {
        // Animate off-screen in swipe direction, then switch image
        const containerWidth = containerRef.current?.offsetWidth || 300
        setDragOffset(diff > 0 ? -containerWidth : containerWidth)
        setIsTransitioning(true)

        swipeTimer.current = setTimeout(() => {
          if (diff > 0) {
            handleNext()
          } else {
            handlePrevious()
          }
          // Snap new image in from opposite side
          setIsTransitioning(false)
          setDragOffset(0)
        }, 200)
      } else {
        // Snap back
        setIsTransitioning(true)
        setDragOffset(0)
        swipeTimer.current = setTimeout(() => setIsTransitioning(false), 200)
      }

      touchStartX.current = null
      touchStartY.current = null
    },
    [handleNext, handlePrevious]
  )

  const activeImageSet = imageSetUrls[activeIndex]
  if (!activeImageSet) {
    return null
  }

  const galleryImageSrc =
    activeImageSet.large ??
    activeImageSet.medium ??
    activeImageSet.small ??
    activeImageSet.xs
  const galleryImageSrcSet = getMenuItemSrcSet(activeImageSet)

  return (
    <div className='productImageGallery'>
      {/* Main Gallery Image with Navigation */}
      <div
        className='galleryImageContainer'
        aria-live='polite'
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {imageSetUrls.length > 1 && (
          <>
            <button
              className='navButton navButtonPrev'
              onClick={handlePrevious}
              aria-label={t('product-display.gallery.previous-image')}
              type='button'
            >
              <ChevronLeftIcon size='md' aria-hidden='true' />
            </button>
            <button
              className='navButton navButtonNext'
              onClick={handleNext}
              aria-label={t('product-display.gallery.next-image')}
              type='button'
            >
              <ChevronRightIcon size='md' aria-hidden='true' />
            </button>
          </>
        )}
        <Image
          src={galleryImageSrc ? formatImageURL(galleryImageSrc) : ''}
          srcSet={galleryImageSrcSet}
          className={classnames('itemGalleryImage', imageAspectRatioClass)}
          style={{
            transform: dragOffset ? `translateX(${dragOffset}px)` : undefined,
            transition: isTransitioning ? 'transform 0.2s ease-out' : undefined
          }}
          alt={t('product-display.gallery.image-alt', {
            itemName,
            current: activeIndex + 1,
            total: imageSetUrls.length
          })}
          onLoad={ooImageAspectRatio ? onImageLoad : undefined}
        />
      </div>

      {/* Dot indicators for mobile swipe */}
      <DotIndicators total={imageSetUrls.length} activeIndex={activeIndex} />

      <ThumbnailGallery
        imageSetUrls={imageSetUrls}
        activeIndex={activeIndex}
        onThumbnailClick={handleThumbnailClick}
      />
    </div>
  )
}
